import axios from "axios";
import { cacheCharts } from "@/lib/chart";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const http = axios.create({ baseURL: API, withCredentials: true });

let refreshing = null;
http.interceptors.response.use(
  (r) => r,
  async (err) => {
    const cfg = err.config;
    if (err.response?.status === 401 && cfg && !cfg._retry && !cfg.url.startsWith("/auth/")) {
      cfg._retry = true;
      refreshing = refreshing || http.post("/auth/refresh").finally(() => { refreshing = null; });
      try { await refreshing; return http(cfg); } catch { /* fall through */ }
    }
    throw err;
  }
);

export function apiError(e) {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg).filter(Boolean).join(" ");
  return null;
}

export const api = {
  me: () => http.get("/auth/me").then((r) => r.data),
  refresh: () => http.post("/auth/refresh").then((r) => r.data),
  login: (email, password) => http.post("/auth/login", { email, password }).then((r) => r.data),
  register: (email, password, name, acceptTerms) => http.post("/auth/register", { email, password, name, acceptTerms }).then((r) => r.data),
  googleSession: (sid) => http.post("/auth/google-session", null, { headers: { "X-Session-ID": sid } }).then((r) => r.data),
  logout: () => http.post("/auth/logout"),
  getSession: () => http.get("/session").then((r) => r.data.session),
  putSession: (session) => http.put("/session", { session }),
  getSites: () => http.get("/sites").then((r) => { cacheCharts(r.data); return r.data; }),
  setActual: (id, actualInches) => http.patch(`/drops/${id}`, { actualInches }).then((r) => r.data),
  scanBol: (image) => http.post("/veeder/bol-scan", { image }, { timeout: 60000 }).then((r) => r.data),
  scanManway: (image, lang, metric) => http.post("/veeder/manway-scan", { image, lang, metric }, { timeout: 60000 }).then((r) => r.data),
  scanStick: (image, lang, metric, mode = "level") => http.post("/veeder/stick-scan", { image, lang, metric, mode }, { timeout: 60000 }).then((r) => r.data),
  scanChart: (image) => http.post("/veeder/chart-scan", { image }, { timeout: 60000 }).then((r) => r.data),
  addSite: (site) => http.post("/sites", site).then((r) => r.data),
  updateSite: (id, site) => http.put(`/sites/${id}`, site).then((r) => r.data),
  removeSite: (id) => http.delete(`/sites/${id}`),
  getDrops: () => http.get("/drops").then((r) => r.data),
  addDrop: (drop) => http.post("/drops", drop).then((r) => r.data),
  removeDrop: (id) => http.delete(`/drops/${id}`),
  donate: (body) => http.post("/donate/checkout", { ...body, origin_url: window.location.origin }).then((r) => r.data),
  paymentStatus: (sid) => http.get(`/payments/status/${sid}`).then((r) => r.data),
  loadList: (k) => http.get(`/${k}`).then((r) => r.data),
  loadAdd: (k, body) => http.post(`/${k}`, body).then((r) => r.data),
  loadUpdate: (k, id, body) => http.put(`/${k}/${id}`, body).then((r) => r.data),
  loadRemove: (k, id) => http.delete(`/${k}/${id}`).then((r) => r.data),
  ustNearby: (lat, lng, radius) => http.get("/ust/nearby", { params: { lat, lng, radius } }).then((r) => r.data),
  ustSearch: (q, place) => http.get("/ust/search", { params: { q, place } }).then((r) => r.data),
  ustCheck: (params) => http.get("/ust/check", { params }).then((r) => r.data),
  ustFacility: (id) => http.get(`/ust/facility/${encodeURIComponent(id)}`).then((r) => r.data),
  reviews: () => http.get("/reviews").then((r) => r.data),
  addReview: (r) => http.post("/reviews", r).then((x) => x.data),
  allReviews: () => http.get("/reviews/all").then((r) => r.data),
  setReview: (id, status) => http.patch(`/reviews/${id}`, { status }).then((r) => r.data),
  deleteReview: (id) => http.delete(`/reviews/${id}`).then((r) => r.data),
  ask: (q) => http.post("/questions", q).then((r) => r.data),
  uploadPhoto: (image) => http.post("/questions/photos", { image }, { timeout: 60000 }).then((r) => r.data),
  questions: () => http.get("/questions").then((r) => r.data),
  setQuestionStatus: (id, status) => http.patch(`/questions/${id}`, { status }),
  replyQuestion: (id, text) => http.post(`/questions/${id}/reply`, { text }).then((r) => r.data),
};
